<template>
  <BottomSheetModal
    v-model="isOpen"
    hide-confirm
    hide-header
    hide-floating-close
    no-padding
    @close="handleClose"
  >
    <!-- Custom floating header buttons -->
    <button class="pcm-float-btn pcm-float-btn--left" @click="handleClose">
      <v-icon size="22">mdi-close</v-icon>
    </button>
    <button
      ref="dotsBtnRef"
      class="pcm-float-btn pcm-float-btn--right"
      :class="{ 'pcm-float-btn--active': showCtxMenu }"
      @click.stop="toggleCtxMenu"
    >
      <v-icon size="22">mdi-dots-vertical</v-icon>
    </button>

    <Teleport to="body">
      <div v-if="showCtxMenu" class="pcm-ctx-overlay" @click="closeCtxMenu" />
      <Transition name="quick-menu">
        <div
          v-if="showCtxMenu"
          class="quick-context-menu"
          :style="ctxMenuStyle"
          @click.stop
        >
          <div class="quick-context-menu-content">
            <template v-if="ctxView === 'more'">
              <div class="quick-menu-item" @click="ctxView = 'main'">
                <span>{{ t('common.back') }}</span>
                <v-icon size="18">mdi-chevron-left</v-icon>
              </div>
              <div class="quick-menu-header">{{ t('report.more') }}</div>
              <div class="quick-menu-item" @click="onBlock">
                <span>{{ t('block.title') }}</span>
                <v-icon size="18">mdi-account-cancel-outline</v-icon>
              </div>
              <div class="quick-menu-divider" />
              <div
                class="quick-menu-item"
                :class="{ 'quick-menu-item--disabled': !userStore.isAuthenticated }"
                @click="onReport"
              >
                <div class="quick-menu-item-text">
                  <span>{{ t('report.reportContent') }}</span>
                  <span
                    v-if="!userStore.isAuthenticated"
                    class="quick-menu-item-sub"
                  >{{ t('report.loginRequired') }}</span>
                </div>
                <v-icon size="18">mdi-flag-outline</v-icon>
              </div>
            </template>
            <template v-else>
              <div class="quick-menu-item" @click="onShare">
                <span>{{ t('share') }}</span>
                <v-icon size="18">mdi-share-variant-outline</v-icon>
              </div>
              <div v-if="!isBlockedRelation" class="quick-menu-divider" />
              <div v-if="!isBlockedRelation" class="quick-menu-item" @click="onSendMessage">
                <span>{{ t('sendMessage') }}</span>
                <v-icon size="18">mdi-message-outline</v-icon>
              </div>
              <template v-if="canEdit">
                <div class="quick-menu-divider" />
                <div class="quick-menu-item" @click="onEdit">
                  <span>{{ t('userProfile.editProfile') }}</span>
                  <v-icon size="18">mdi-pencil-outline</v-icon>
                </div>
              </template>
              <div class="quick-menu-divider" />
              <div class="quick-menu-item" @click="ctxView = 'more'">
                <span>{{ t('report.more') }}</span>
                <v-icon size="18">mdi-chevron-right</v-icon>
              </div>
            </template>
          </div>
        </div>
      </Transition>
    </Teleport>

    <PlayerProfileContent
      v-if="detail.user"
      :user="detail.user"
      :season-year="detail.seasonYear"
      :season-blocks="detail.seasonBlocks"
      :career-blocks="detail.careerBlocks"
      :all-season-stats="detail.allSeasonStats"
      :league-id="leagueId"
    />
    <v-snackbar
      v-model="showShareSnackbar"
      location="top"
      color="black"
      timeout="2500"
      :style="{ zIndex: 99999 }"
      class="share-snackbar-top"
    >
      <v-icon class="me-2">mdi-content-copy</v-icon>{{ t('linkCopied') }}
    </v-snackbar>

    <!-- Report modal -->
    <ReportModal v-model="showReportModal" :override-target="reportOverrideTarget" />

    <!-- Block modal -->
    <BlockUserModal v-model="showBlockModal" :target="blockTarget" @blocked="onBlocked" />

    <v-snackbar
      v-model="blockedSnackbar"
      location="top"
      color="black"
      timeout="2500"
      :style="{ zIndex: 99999 }"
      class="share-snackbar-top"
    >
      <v-icon class="me-2">mdi-account-cancel-outline</v-icon>{{ t('block.successText') }}
    </v-snackbar>
  </BottomSheetModal>
</template>

<script setup lang="ts">
import { ref, nextTick, onBeforeUnmount, computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import BottomSheetModal from '@/components/BottomSheetModal.vue'
import PlayerProfileContent from '@/components/PlayerProfileContent.vue'
import ReportModal from '@/components/ReportModal.vue'
import BlockUserModal from '@/components/BlockUserModal.vue'
import axiosInstance from '@/services/axiosInstance'
import { type StatsBlock, buildBlocks } from '@/composables/usePlayerStats'
import { useShareLink } from '@/composables/useShareLink'
import { useChatStore } from '@/stores/chat/chatStore'
import { useUserStore } from '@/stores/user/userStore'
import { isMyUser } from '@/utils/userUtils'
import { getUserAvatarUrl } from '@/utils/imageUtils'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000'
const { t } = useI18n()
const router = useRouter()
const chatStore = useChatStore()
const userStore = useUserStore()

defineProps<{
  leagueId?: string | null
}>()

const emit = defineEmits<{
  (e: 'close'): void
}>()

const isOpen = ref(false)

const detail = ref<{
  user: any | null
  seasonYear: string
  seasonBlocks: StatsBlock[]
  careerBlocks: StatsBlock[]
  allSeasonStats: any[]
  loading: boolean
}>({
  user: null,
  seasonYear: '',
  seasonBlocks: [],
  careerBlocks: [],
  allSeasonStats: [],
  loading: false,
})

/* ── Context menu ── */
const showCtxMenu = ref(false)
const ctxView = ref<'main' | 'more'>('main')
const dotsBtnRef = ref<HTMLElement | null>(null)
const ctxMenuStyle = ref<Record<string, string>>({})

function toggleCtxMenu() {
  if (showCtxMenu.value) {
    closeCtxMenu()
    return
  }
  ctxView.value = 'main'
  const rect = dotsBtnRef.value?.getBoundingClientRect()
  if (rect) {
    ctxMenuStyle.value = {
      position: 'fixed',
      top: `${rect.bottom + 6}px`,
      right: `${window.innerWidth - rect.right}px`,
      zIndex: '100000',
    }
  }
  showCtxMenu.value = true
}

function closeCtxMenu() {
  showCtxMenu.value = false
  ctxView.value = 'main'
}

onBeforeUnmount(() => closeCtxMenu())

/* ── Report ── */
const showReportModal = ref(false)
const reportOverrideTarget = ref<any>(null)

function onReport() {
  if (!userStore.isAuthenticated) return
  const u = detail.value.user
  if (!u) return
  const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.nickName || u.name || 'Uživatel'
  const initials = ((u.firstName?.[0] || '') + (u.lastName?.[0] || '')).toUpperCase() || '?'
  reportOverrideTarget.value = {
    kind: 'user',
    kindLabel: t('report.targets.user'),
    ref: u._id,
    name: fullName,
    image: getUserAvatarUrl(u) || undefined,
    initials,
  }
  closeCtxMenu()
  nextTick(() => {
    showReportModal.value = true
  })
}

/* ── Send message ── */
const isBlockedRelation = computed(() => {
  const r = (detail.value.user as any)?.relation
  return !!(r && (r.iBlocked || r.theyBlockedMe))
})
async function onSendMessage() {
  const u = detail.value.user
  if (!u?._id) return
  closeCtxMenu()
  try {
    const conv = await chatStore.createDirectConversation(u._id)
    isOpen.value = false
    emit('close')
    if (conv?._id) router.push(`/messages/${conv._id}`)
  } catch (err) {
    console.error('[PlayerCardModal] createDirectConversation failed:', err)
  }
}

/* ── Edit profile ── */
const canEdit = ref(false)

async function refreshCanEdit(userId: string | null | undefined) {
  if (!userId || !userStore.isAuthenticated) {
    canEdit.value = false
    return
  }
  // Owner shortcut
  if (userStore.user?._id === userId) {
    canEdit.value = true
    return
  }
  try {
    canEdit.value = !!(await isMyUser(userId))
  } catch {
    canEdit.value = false
  }
}

function onEdit() {
  const u = detail.value.user
  if (!u?._id) return
  closeCtxMenu()
  isOpen.value = false
  emit('close')
  router.push({ path: `/users/${u._id}`, query: { edit: '1' } })
}

/* ── Block ── */
const showBlockModal = ref(false)
const blockedSnackbar = ref(false)
const blockTarget = ref<{ id: string; name: string; image?: string; initials?: string } | null>(null)

function onBlock() {
  const u = detail.value.user
  if (!u?._id) return
  const fullName = [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.nickName || u.name || 'Uživatel'
  const initials = ((u.firstName?.[0] || '') + (u.lastName?.[0] || '')).toUpperCase() || '?'
  blockTarget.value = {
    id: u._id,
    name: fullName,
    image: getUserAvatarUrl(u) || undefined,
    initials,
  }
  closeCtxMenu()
  nextTick(() => {
    showBlockModal.value = true
  })
}

function onBlocked(_userId: string) {
  blockedSnackbar.value = true
}

/* ── Share ── */
function onShare() {
  closeCtxMenu()
  nextTick(() => sharePlayerLink())
}

/* ── Public method to open the modal ── */
async function open(player: any) {
  detail.value = {
    user: player,
    seasonYear: '',
    seasonBlocks: [],
    careerBlocks: [],
    allSeasonStats: [],
    loading: true,
  }
  isOpen.value = true
  refreshCanEdit(player?._id)

  try {
    const { data } = await axiosInstance.get(`${API_BASE_URL}/stats/user/${player._id}`)
    const u = data.user || {}
    detail.value.user = { ...player, ...u }
    detail.value.seasonYear = data.bestSeason?.seasonId?.year || ''
    detail.value.seasonBlocks = buildBlocks(data.bestSeason?.stats?.total)
    detail.value.careerBlocks = buildBlocks(data.overall?.stats?.total)
    detail.value.allSeasonStats = data.seasonStats || []
  } catch {
    /* ignored */
  } finally {
    detail.value.loading = false
  }

  // Fetch block relation (independent of stats)
  if (userStore.isAuthenticated && userStore.user?._id !== player._id) {
    try {
      const { data: userData } = await axiosInstance.get(`${API_BASE_URL}/users/${player._id}`)
      if (userData?.relation && detail.value.user) {
        ;(detail.value.user as any).relation = userData.relation
      }
    } catch {
      /* ignored */
    }
  }
}

function handleClose() {
  isOpen.value = false
  closeCtxMenu()
  emit('close')
}

function buildUserPath(u: any): string {
  if (!u?._id) return '/'
  const sn: string = u.searchName || `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim()
  const parts = sn.split(' ').filter(Boolean)
  if (parts.length >= 2) return `/users/${u._id}-${parts[0]}-${parts[1]}`
  return `/users/${u._id}`
}

const { share: sharePlayerLink, showSnackbar: showShareSnackbar } = useShareLink({
  path: () => buildUserPath(detail.value.user),
  title: () => {
    const u = detail.value.user
    return [u?.firstName, u?.lastName].filter(Boolean).join(' ') || u?.name || 'Player'
  },
})

defineExpose({ open })
</script>

<style lang="scss" scoped>
.pcm-float-btn {
  position: absolute;
  top: 12px;
  z-index: 10;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(var(--v-theme-sheetCloseBtn), 0.65);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid transparent;
  color: rgb(var(--v-theme-sheetText));
  cursor: pointer;
  transition: transform 0.15s ease, filter 0.2s ease;

  &--left { left: 12px; }
  &--right { right: 12px; }

  &:active { transform: scale(0.95); }
  &:hover { filter: brightness(0.92); }
  &--active { filter: brightness(0.85); }

  :deep(.v-icon) {
    color: rgb(var(--v-theme-sheetText)) !important;
  }
}

.pcm-ctx-overlay {
  position: fixed;
  inset: 0;
  z-index: 99998;
  background: transparent;
}
</style>

<style lang="scss">
/* Global context menu styles — mirror QuickActionsMenu (.quick-context-menu) */
.quick-context-menu {
  position: fixed;
  min-width: 220px;
  max-width: 260px;
  max-height: calc(100vh - 24px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  background: #161E30;
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 16px;
  box-shadow:
    0 24px 64px rgba(0, 0, 0, 0.6),
    0 8px 24px rgba(0, 0, 0, 0.4),
    0 -1px 0 0 rgba(255, 255, 255, 0.06) inset;
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  padding: 4px;
  z-index: 99999;
}

body:has(.v-theme--light) .quick-context-menu {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow:
    0 24px 64px rgba(0, 0, 0, 0.12),
    0 8px 24px rgba(0, 0, 0, 0.06),
    0 -1px 0 0 rgba(255, 255, 255, 0.8) inset;
}

.quick-context-menu-content { padding: 0; }

.quick-menu-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  color: rgba(255, 255, 255, 0.65);
  font-size: 14px;
  font-weight: 600;
  user-select: none;
  -webkit-tap-highlight-color: transparent;

  &:hover {
    background: rgba(255, 255, 255, 0.07);
    color: #ffffff;
    .v-icon { color: #ffffff !important; opacity: 1; }
  }
  &:active {
    background: rgba(255, 255, 255, 0.04);
    transform: scale(0.98);
  }
  .v-icon {
    color: rgba(255, 255, 255, 0.35) !important;
    transition: color 0.15s ease, opacity 0.15s ease;
    flex-shrink: 0;
  }
  span { font-size: 14px; font-weight: 600; }

  &.quick-menu-item--disabled {
    cursor: not-allowed;
    opacity: 0.55;
    &:hover {
      background: transparent;
      color: rgba(255, 255, 255, 0.65);
      .v-icon { color: rgba(255, 255, 255, 0.35) !important; opacity: 1; }
    }
    &:active { transform: none; background: transparent; }
  }

  .quick-menu-item-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
    flex: 1;
  }
  .quick-menu-item-sub {
    font-size: 11px;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.45);
    line-height: 1.25;
  }
}

body:has(.v-theme--light) .quick-menu-item {
  color: rgba(0, 0, 0, 0.7);
  .v-icon { color: rgba(0, 0, 0, 0.4) !important; }
  &:hover {
    background: rgba(0, 0, 0, 0.06);
    color: #000000;
    .v-icon { color: #000000 !important; }
  }
  &:active { background: rgba(0, 0, 0, 0.04); }

  .quick-menu-item-sub { color: rgba(0, 0, 0, 0.5); }

  &.quick-menu-item--disabled:hover {
    background: transparent;
    color: rgba(0, 0, 0, 0.7);
    .v-icon { color: rgba(0, 0, 0, 0.4) !important; }
  }
}

.quick-menu-divider {
  height: 1px;
  background: linear-gradient(90deg,
    transparent 0%,
    rgba(255, 255, 255, 0.08) 20%,
    rgba(255, 255, 255, 0.08) 80%,
    transparent 100%);
  margin: 3px 0;
}

.quick-menu-header {
  padding: 8px 12px 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: rgba(255, 255, 255, 0.35);
  user-select: none;

  &:not(:first-child) {
    margin-top: 4px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    padding-top: 10px;
  }
}

body:has(.v-theme--light) .quick-menu-divider {
  background: linear-gradient(90deg,
    transparent 0%,
    rgba(0, 0, 0, 0.08) 20%,
    rgba(0, 0, 0, 0.08) 80%,
    transparent 100%);
}

body:has(.v-theme--light) .quick-menu-header {
  color: rgba(0, 0, 0, 0.4);
  &:not(:first-child) { border-top-color: rgba(0, 0, 0, 0.06); }
}

.quick-menu-enter-active { transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1); }
.quick-menu-leave-active { transition: all 0.18s ease; }
.quick-menu-enter-from { opacity: 0; transform: translateY(16px) scale(0.92); }
.quick-menu-leave-to { opacity: 0; transform: translateY(8px) scale(0.96); }
</style>
